import React, { useEffect, useState } from 'react';
import { Plus, X, Save, Check, Trash2 } from 'lucide-react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import FormControl from '../../common/FormControl';
import { AggregationTarget, ColumnDataType, DataSet, DataSource, OperatorCategory, OperatorKind, RiskOption, Rule } from '../types';
import { OPERATOR_CATEGORIES, colDataTypeToLegacy, resolveColType, operatorByKind, operatorsFor } from '../operators';
import FileAnalyticsAPI from '../fileAnalyticsService';
import { useFieldValidation } from '../../../validation/useFieldValidation';
import { ValidatedInput } from '../../../validation/ValidatedFields';
import { usePermission, NOT_AUTHORISED_MSG } from '../../../contexts/PermissionContext';

interface RuleBuilderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  /** When editing, a Delete button appears in the footer and calls this. */
  onDelete?: () => void;
  collectionTypeId?: string;
  /** Name of the pipeline/source this rule belongs to — shown in the modal heading. */
  collectionName?: string;
  mode?: 'define' | 'run';
  rule?: Rule | null;
}

interface DraftStep {
  tempId: string;
  target_dataset_id: string;
  column: string;
  column_type: ColumnDataType;
  operator: OperatorKind;
  params: string[];
  negate: boolean;
  description?: string;
}

// Operators that already express negation — hide the NOT toggle for these.
const ALREADY_NEGATED_OPS = new Set<OperatorKind>([
  'neq', 'not_in_list', 'is_not_null', 'hour_not_between', 'date_diff_years_not_between',
  'not_contains', 'is_not_empty', 'not_in_dataset', 'is_past',
]);

// Operators whose first parameter is ANOTHER column (render a column picker, not free text).
const COL_PARAM_OPS = new Set<OperatorKind>(['col_gt', 'col_gte', 'col_lt', 'col_lte', 'col_eq', 'col_neq']);
// Operators where param[0] is another date column and param[1] is a day count.
const DATE_DIFF_DAY_OPS = new Set<OperatorKind>(['date_diff_days_ge', 'date_diff_days_le', 'date_diff_days_gt', 'date_diff_days_lt']);
// Two-column operators: param[0]=ref_col, param[1]=threshold
const COL_PCT_OPS = new Set<OperatorKind>(['col_pct_above', 'percent_change_gt']);

const PERCENT_VALUE_OPS = new Set<OperatorKind>(['gt', 'gte', 'lt', 'lte', 'eq', 'neq']);

// Single plain-number param operators
const NUMBER_SINGLE_OPS = new Set<OperatorKind>([
  'within_last_days', 'top_n', 'bottom_n', 'std_dev_above', 'std_dev_below',
  'z_score_gt', 'iqr_outlier', 'is_round_number', 'days_since_gt', 'days_since_gte',
]);
// Operators whose double params are both hour numbers (no column picker needed).
const HOUR_RANGE_OPS = new Set<OperatorKind>(['hour_between', 'hour_not_between']);

// date_diff_years_between/not_between: param_shape='csv' but rendered as structured
// date col picker + two year-number inputs (not a raw chip input).
const DATE_DIFF_YEAR_OPS = new Set<OperatorKind>(['date_diff_years_between', 'date_diff_years_not_between']);

// Operators whose csv chips must be YYYY-MM-DD dates (is_nonworking_day holidays).
const DATE_CSV_OPS = new Set<OperatorKind>(['is_nonworking_day']);

const pctToDisplay = (v: string): string => {
  if (v === '' || v == null) return '';
  const n = Number(v);
  return Number.isFinite(n) ? String(Number((n * 100).toFixed(6))) : v;
};
const pctToStore = (v: string): string => {
  if (v === '' || v == null) return '';
  const n = Number(v);
  return Number.isFinite(n) ? String(n / 100) : v;
};

const mkStep = (datasetId: string, op: OperatorKind = 'gt'): DraftStep => ({
  tempId: Math.random().toString(36).slice(2),
  target_dataset_id: datasetId,
  column: '',
  column_type: 'continuous',
  operator: op,
  params: [''],
  negate: false,
  description: '',
});

const stepsFromRule = (rule: Rule): DraftStep[] =>
  rule.steps.map(s => ({
    tempId: Math.random().toString(36).slice(2),
    target_dataset_id: s.target_dataset_id,
    column: s.column,
    column_type: s.column_type,
    // A persisted step may carry a retired operator kind (cross/group, back-compat).
    // Cast at the boundary; the dropdown simply shows blank for a retired kind.
    operator: s.operator as OperatorKind,
    params: s.params ?? [''],
    negate: s.negate === true,
    description: s.description ?? '',
  }));

/** Build operator dropdown options with category prefix labels, ordered by category. */
const buildGroupedOpOptions = (ops: ReturnType<typeof operatorsFor>): { value: string; label: string }[] => {
  const catLabel: Record<OperatorCategory, string> = Object.fromEntries(
    OPERATOR_CATEGORIES.map(c => [c.key, c.label])
  ) as Record<OperatorCategory, string>;
  return ops.map(o => ({ value: o.kind, label: `${catLabel[o.category]} · ${o.label}` }));
};

/** Minimal chip / tag input that stores its value as a comma-joined string. */
const ChipInput: React.FC<{
  value: string;
  onChange: (joined: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** When provided, each chip value is tested. Rejected chips show an inline error. */
  validateChip?: (v: string) => string | null;
  'data-testid'?: string;
}> = ({ value, onChange, placeholder, disabled, validateChip, 'data-testid': testId }) => {
  const chips = value ? value.split(',').map(s => s.trim()).filter(Boolean) : [];
  const [draft, setDraft] = React.useState('');
  const [chipError, setChipError] = React.useState<string | null>(null);

  const addChip = () => {
    const v = draft.trim();
    if (!v) return;
    if (validateChip) {
      const err = validateChip(v);
      if (err) { setChipError(err); return; }
    }
    setChipError(null);
    const next = [...chips, v];
    onChange(next.join(','));
    setDraft('');
  };

  const removeChip = (i: number) => {
    const next = chips.filter((_, ci) => ci !== i);
    onChange(next.join(','));
  };

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', border: `1px solid ${chipError ? 'var(--danger, #d32f2f)' : 'var(--border-subtle)'}`, borderRadius: 6, padding: '4px 8px', background: disabled ? 'var(--surface-disabled, var(--bg-card))' : 'var(--surface-1, #fff)', minHeight: 36 }}>
        {chips.map((chip, i) => (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: 'var(--brand-subtle, #e8f4ff)', color: 'var(--brand)', borderRadius: 4, padding: '1px 6px', fontSize: 12 }}>
            {chip}
            {!disabled && (
              <button type="button" onClick={() => removeChip(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, color: 'inherit' }} data-testid={`BTN_fa_rule_chip_remove_${i}`} aria-label={`Remove ${chip}`}>
                <X size={10} />
              </button>
            )}
          </span>
        ))}
        {!disabled && (
          <input
            type="text"
            value={draft}
            onChange={e => { setDraft(e.target.value); setChipError(null); }}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addChip(); } }}
            onBlur={addChip}
            placeholder={chips.length === 0 ? (placeholder ?? 'Type and press Enter') : 'Add more…'}
            disabled={disabled}
            data-testid={testId}
            style={{ border: 'none', outline: 'none', fontSize: 13, flex: '1 1 80px', minWidth: 80, background: 'transparent', padding: '2px 0' }}
          />
        )}
      </div>
      {chipError && <div style={{ fontSize: 11, color: 'var(--danger, #d32f2f)', marginTop: 2 }}>{chipError}</div>}
    </div>
  );
};

// Litigation-style group box: legend-on-the-border fieldset, no hint subtext.
// hint/first kept in the prop type (call sites still pass them) but intentionally
// unused — sections are now enclosed boxes, not divider-separated rows.
const StepGroup: React.FC<{ title: string; hint?: string; first?: boolean; children: React.ReactNode }> = ({ title, children }) => (
  <fieldset className="field-group">
    <legend className="field-group-legend">{title}</legend>
    <div className="field-group-body">{children}</div>
  </fieldset>
);


type ColOpt = { value: string; label: string; type: ColumnDataType };

const RuleBuilderModal: React.FC<RuleBuilderModalProps> = ({ isOpen, onClose, onSaved, onDelete, collectionTypeId, collectionName, mode = 'define', rule = null }) => {
  const isPipelineMode = !!collectionTypeId;
  const fieldVh = useFieldValidation();
  const { allowed: canModify, loading: permLoading } = usePermission('CMG_RULES', 'view+modify');

  const [datasets, setDatasets] = useState<DataSet[]>([]);
  // Pipeline mode: the rule is authored against ONE of the pipeline's linked data sources.
  // The data source is chosen at create time and locked thereafter.
  const [noPipelineData, setNoPipelineData] = useState(false);
  const [linkedSources, setLinkedSources] = useState<DataSource[]>([]);
  // Single TARGET selection. Prefixed value: `ds:<dataSourceId>` for a plain/enriched
  // source, `agg:<outputDatasetId>` for an aggregated dataset (CMR-RT). One picker.
  const [targetSel, setTargetSel] = useState<string>('');
  // Enriched columns derived from the bound source's Enrich/Combine links (name → type).
  const [enrichedCols, setEnrichedCols] = useState<ColOpt[]>([]);
  // CMR-RT — aggregated datasets offered as rule targets ("Aggregated: «name»").
  const [aggTargets, setAggTargets] = useState<AggregationTarget[]>([]);

  // Derived: the selected data source id (empty when an aggregated target is chosen).
  const dataSourceId = targetSel.startsWith('ds:') ? targetSel.slice(3) : '';
  // Derived: the selected aggregated target (undefined when a data source is chosen).
  const selectedAggTarget = targetSel.startsWith('agg:')
    ? aggTargets.find(t => String(t.output_dataset_id) === targetSel.slice(4))
    : undefined;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<'Critical' | 'High' | 'Medium' | 'Low'>('Medium');
  const [steps, setSteps] = useState<DraftStep[]>([]);
  const [riskId, setRiskId] = useState<number | null>(null);
  const [riskOptions, setRiskOptions] = useState<RiskOption[]>([]);
  // dataset_ref param shape: list of pipelines + per-step ref columns
  const [refPipelines, setRefPipelines] = useState<{ value: string; label: string }[]>([]);
  const [refCols, setRefCols] = useState<Record<string, { value: string; label: string }[]>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    fieldVh.reset();
    const editing = !!rule;
    setName(editing ? rule!.name : '');
    setDescription(editing ? (rule!.description ?? '') : '');
    setSeverity(editing ? rule!.severity : 'Medium');
    setRiskId(editing ? (rule!.risk_id ?? null) : null);
    setError(null);
    setNoPipelineData(false);
    setEnrichedCols([]);
    setAggTargets([]);
    setTargetSel('');
    FileAnalyticsAPI.listRisks().then(setRiskOptions).catch(() => setRiskOptions([]));
    FileAnalyticsAPI.listCollectionTypes().then(cts =>
      setRefPipelines(cts.map(c => ({ value: String(c.id), label: c.name })))
    ).catch(() => setRefPipelines([]));
    (async () => {
      try {
        if (isPipelineMode) {
          // Single-source pipeline mode: the rule reads ONE of the pipeline's linked data sources
          // OR an aggregated dataset (CMR-RT). Columns come from that target.
          const links = await FileAnalyticsAPI.getPipelineDataSources(collectionTypeId!);
          setLinkedSources(links);
          // CMR-RT — fetch aggregated targets for this pipeline.
          const aggs = await FileAnalyticsAPI.listAggregationTargets(collectionTypeId!).catch(() => [] as AggregationTarget[]);
          setAggTargets(aggs);

          if (editing && rule!.agg_target_dataset_id != null) {
            // Seed an aggregated target.
            setTargetSel(`agg:${rule!.agg_target_dataset_id}`);
          } else if (editing) {
            // Seed the single data source from the existing rule.
            const initialDs = rule!.data_source_id != null
              ? String(rule!.data_source_id)
              : (links[0] ? String(links[0].id) : '');
            setTargetSel(initialDs ? `ds:${initialDs}` : '');
            if (initialDs) await loadEnriched(initialDs, links);
          } else {
            // New rule — default to the first linked source.
            const initialDs = links[0] ? String(links[0].id) : '';
            setTargetSel(initialDs ? `ds:${initialDs}` : '');
            if (initialDs) await loadEnriched(initialDs, links);
          }
          setNoPipelineData(false);
          setSteps(editing ? stepsFromRule(rule!) : [mkStep('')]);
        } else {
          const list = await FileAnalyticsAPI.listDatasets();
          const ready = list.filter(d => d.status === 'Ready');
          setDatasets(ready);
          setSteps(editing ? stepsFromRule(rule!) : [mkStep(ready[0]?.id ?? '')]);
        }
      } catch (err: any) {
        setError(err?.response?.data?.detail || 'Failed to load pipeline');
      }
    })();
  }, [isOpen, rule, isPipelineMode, collectionTypeId]);

  // ── Enriched-column loader (Enrich selected_columns + Combine ref schema) ──
  //
  // A rule may reference the source's ENRICHED columns, not only its base
  // standard_schema. Enrich links contribute their selected_columns; Combine
  // links contribute the ref source's full schema. Enriched columns default to a
  // permissive type unless we can infer a stricter type from the ref source's schema.
  const loadEnriched = async (dsId: string, sources: DataSource[]) => {
    try {
      const links = await FileAnalyticsAPI.listSourceLinks(Number(dsId));
      // Enrich/Combine refs are standalone sources, NOT necessarily pipeline-linked,
      // so resolve their schemas from ALL entity sources — otherwise an enriched
      // numeric column (e.g. invoice_amount) defaults to 'text'.
      const allSources = await FileAnalyticsAPI.listSources().catch(() => [] as DataSource[]);
      const byId = new Map([...sources, ...allSources].map(s => [String(s.id), s]));
      const out: ColOpt[] = [];
      const seen = new Set<string>();
      for (const link of links) {
        const ref = byId.get(String(link.ref_source_id));
        const refSchema = ref?.standard_schema ?? [];
        const refType = (n: string): ColumnDataType =>
          resolveColType(refSchema.find(c => c.name === n) ?? { name: n });
        if (link.role === 'enrich') {
          for (const n of (link.selected_columns ?? [])) {
            if (!seen.has(n)) { seen.add(n); out.push({ value: n, label: n, type: refType(n) }); }
          }
        } else if (link.role === 'combine') {
          for (const c of refSchema) {
            const ct = resolveColType(c);
            if (!seen.has(c.name)) { seen.add(c.name); out.push({ value: c.name, label: c.name, type: ct }); }
          }
        }
      }
      setEnrichedCols(out);
    } catch {
      setEnrichedCols([]);
    }
  };

  // ── Column helpers (pipeline mode: single data source) ─────────────────────

  /** Columns from the selected data source's defined schema PLUS its enriched columns. */
  const pipelineColOptions = (): ColOpt[] => {
    // Aggregated-dataset target (CMR-RT): columns come from the aggregation output schema.
    if (selectedAggTarget) {
      return selectedAggTarget.columns.map(c => ({
        value: c.name, label: c.name, type: resolveColType(c as { data_type?: string | null; measurement_level?: string | null; name: string }),
      }));
    }
    if (!isPipelineMode || !dataSourceId) return [];
    const ds = linkedSources.find(l => String(l.id) === dataSourceId);
    if (!ds) return [];
    const base: ColOpt[] = (ds.standard_schema ?? []).map(c => ({ value: c.name, label: c.name, type: resolveColType(c) }));
    // Dedup by name; base schema wins (authoritative semantic type).
    const seen = new Set(base.map(c => c.value));
    const merged = [...base];
    for (const c of enrichedCols) if (!seen.has(c.value)) { seen.add(c.value); merged.push(c); }
    return merged;
  };

  // ── Legacy single-source column helper (non-pipeline mode) ─────────────────

  const colsForStep = (step: DraftStep): { name: string; type: ColumnDataType }[] => {
    if (isPipelineMode) return [];
    const ds = datasets.find(d => d.id === step.target_dataset_id);
    return (ds?.columns ?? []).map(c => ({ name: c.name, type: c.data_type as ColumnDataType }));
  };

  const loadRefCols = async (tempId: string, pipelineId: string) => {
    try {
      const sources = await FileAnalyticsAPI.getPipelineDataSources(pipelineId);
      const cols: { value: string; label: string }[] = [];
      const seen = new Set<string>();
      for (const src of sources) {
        for (const c of (src.standard_schema ?? [])) {
          if (!seen.has(c.name)) { seen.add(c.name); cols.push({ value: c.name, label: c.name }); }
        }
      }
      setRefCols(prev => ({ ...prev, [tempId]: cols }));
    } catch { setRefCols(prev => ({ ...prev, [tempId]: [] })); }
  };

  const addStep = () => setSteps(prev => [...prev, mkStep(isPipelineMode ? '' : (datasets[0]?.id ?? ''))]);
  const removeStep = (idx: number) => {
    setSteps(prev => prev.filter((_, i) => i !== idx));
  };
  const update = (idx: number, patch: Partial<DraftStep>) =>
    setSteps(prev => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));

  const onDatasetChange = (idx: number, dsId: string) => {
    update(idx, { target_dataset_id: dsId, column: '', column_type: 'continuous', operator: 'gt', params: [''] });
  };

  // ── Column change ──────────────────────────────────────────────────────────

  const onColumnChange = (idx: number, col: string) => {
    if (isPipelineMode) {
      const allOpts = pipelineColOptions();
      const found = allOpts.find(o => o.value === col);
      const ct = (found?.type ?? 'text') as ColumnDataType;
      const firstOp = operatorsFor(ct)[0];
      update(idx, {
        column: col,
        column_type: ct,
        operator: firstOp?.kind ?? 'eq',
        params: firstOp?.param_shape === 'none' ? [] : firstOp?.param_shape === 'double' ? ['', ''] : [''],
      });
    } else {
      const found = colsForStep(steps[idx]).find(c => c.name === col);
      const ct = (found?.type ?? 'text') as ColumnDataType;
      const firstOp = operatorsFor(ct)[0];
      update(idx, {
        column: col,
        column_type: ct,
        operator: firstOp?.kind ?? 'eq',
        params: firstOp?.param_shape === 'none' ? [] : firstOp?.param_shape === 'double' ? ['', ''] : [''],
      });
    }
  };

  const onOperatorChange = (idx: number, opKind: OperatorKind) => {
    const op = operatorByKind(opKind);
    let params: string[] = [];
    if (op?.param_shape === 'single' || op?.param_shape === 'csv') params = [''];
    else if (op?.param_shape === 'double') params = ['', ''];
    else if (op?.param_shape === 'dataset_ref') params = ['', ''];
    update(idx, { operator: opKind, params });
  };

  // ── Submit ────────────────────────────────────────────────────────────────

  const submit = async () => {
    if (!canModify) { setError(NOT_AUTHORISED_MSG); return; }
    const specs = [
      { name: 'name', code: 109, value: name, required: true },
      { name: 'description', code: 110, value: description },
    ];
    const valid = fieldVh.validateAll(specs);
    if (!valid) { setError('Fix the highlighted fields'); return; }
    if (steps.length === 0) { setError('Add at least one condition'); return; }
    if (steps.some(s => !s.column)) { setError('Every condition needs a column'); return; }

    // Param completeness check — each operator declares what it needs.
    for (let i = 0; i < steps.length; i++) {
      const s = steps[i];
      const meta = operatorByKind(s.operator);
      const pos = `Condition ${i + 1}`;
      if (!meta) continue;
      const isDateCol = s.column_type === 'date';
      const isDateVal = (v: string) => /^\d{4}-\d{2}-\d{2}$/.test(v);
      if (meta.param_shape === 'single' || meta.param_shape === 'csv') {
        if (!s.params[0]?.trim()) { setError(`${pos}: value is required`); return; }
        if (isDateCol && !COL_PARAM_OPS.has(s.operator) && !NUMBER_SINGLE_OPS.has(s.operator) && !isDateVal(s.params[0] ?? '')) {
          setError(`${pos}: date must be in YYYY-MM-DD format`); return;
        }
      }
      if (meta.param_shape === 'double') {
        if (!s.params[0]?.trim()) { setError(`${pos}: first value is required`); return; }
        if (!s.params[1]?.trim()) { setError(`${pos}: second value is required`); return; }
        if (isDateCol && !DATE_DIFF_DAY_OPS.has(s.operator) && !HOUR_RANGE_OPS.has(s.operator)) {
          if (!isDateVal(s.params[0] ?? '')) { setError(`${pos}: first date must be in YYYY-MM-DD format`); return; }
          if (!isDateVal(s.params[1] ?? '')) { setError(`${pos}: second date must be in YYYY-MM-DD format`); return; }
          if (s.params[0] >= s.params[1]) { setError(`${pos}: 'from' date must be before 'to' date`); return; }
        }
        // Hour range: both values must be 0–23 and from < to
        if (HOUR_RANGE_OPS.has(s.operator)) {
          const h0 = Number(s.params[0]), h1 = Number(s.params[1]);
          if (h0 < 0 || h0 > 23 || h1 < 0 || h1 > 23) { setError(`${pos}: hours must be between 0 and 23`); return; }
          if (h0 >= h1) { setError(`${pos}: 'from' hour must be less than 'to' hour`); return; }
        }
        // length_between: from < to
        if (s.operator === 'length_between') {
          if (Number(s.params[0]) >= Number(s.params[1])) { setError(`${pos}: lower bound must be less than upper bound`); return; }
        }
        // Date diff: reference column must be selected
        if (DATE_DIFF_DAY_OPS.has(s.operator) && !s.params[0]?.trim()) {
          setError(`${pos}: a reference date column is required`); return;
        }
      }
      // Column comparison: reference column required
      if (COL_PARAM_OPS.has(s.operator) && !s.params[0]?.trim()) {
        setError(`${pos}: a reference column is required`); return;
      }
      // dataset_ref: both pipeline and column required
      if (meta.param_shape === 'dataset_ref') {
        if (!s.params[0]?.trim()) { setError(`${pos}: select a reference pipeline`); return; }
        if (!s.params[1]?.trim()) { setError(`${pos}: select a column from the reference pipeline`); return; }
      }
      // Positive-only number operators
      if (NUMBER_SINGLE_OPS.has(s.operator)) {
        const n = Number(s.params[0]);
        if (!s.params[0]?.trim() || isNaN(n) || n <= 0) { setError(`${pos}: value must be a positive number`); return; }
      }
      // date_diff_years_between/not_between: csv param holds "dateCol,lo,hi" —
      // validate the three parts from params[0] (comma-joined).
      if (DATE_DIFF_YEAR_OPS.has(s.operator)) {
        const raw = s.params[0] ?? '';
        const parts = raw.split(',').map(p => p.trim());
        if (!parts[0]) { setError(`${pos}: select a reference date column`); return; }
        if (!parts[1] || isNaN(Number(parts[1]))) { setError(`${pos}: low year must be a number`); return; }
        if (!parts[2] || isNaN(Number(parts[2]))) { setError(`${pos}: high year must be a number`); return; }
        if (Number(parts[1]) >= Number(parts[2])) { setError(`${pos}: low year must be less than high year`); return; }
      }
      // regex: validate the pattern is a legal JS regular expression.
      if (s.operator === 'regex') {
        try { new RegExp(s.params[0] ?? ''); } catch { setError(`${pos}: invalid regular expression`); return; }
        if (!s.params[0]?.trim()) { setError(`${pos}: regex pattern is required`); return; }
      }
    }

    // Container model: when the pipeline has linked data sources, the rule must pick one.
    if (isPipelineMode && (linkedSources.length > 0 || aggTargets.length > 0) && !targetSel) {
      setError('Choose the data source or aggregated dataset this rule reads'); return;
    }
    // A pipeline rule may be DEFINED before any data is uploaded — at run time its source
    // dataset is resolved from the pipeline's latest file. Only the legacy/DA dataset
    // picker requires a dataset chosen up front.
    if (!isPipelineMode && steps.some(s => !s.target_dataset_id)) {
      setError('Every condition needs a target dataset');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const stepsPayload = steps.map(s => ({
        target_dataset_id: s.target_dataset_id || null,
        column_name: s.column,
        column_type: s.column_type,
        operator: s.operator,
        params: s.params,
        negate: s.negate,
        description: s.description || undefined,
      }));

      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
        severity,
        tags: [] as string[],
        activate: true,
        collection_type_id: collectionTypeId ? Number(collectionTypeId) : undefined,
        risk_id: riskId,
        steps: stepsPayload,
        data_source_id: dataSourceId ? Number(dataSourceId) : undefined,
        agg_target_dataset_id: selectedAggTarget ? selectedAggTarget.output_dataset_id : undefined,
      };

      if (rule) {
        await FileAnalyticsAPI.updateRule(rule.id, payload);
      } else {
        await FileAnalyticsAPI.createRule(payload);
      }
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const showDatasetPicker = !isPipelineMode;
  const isLocked = !!rule;

  // ── Derived column options for condition rows ──────────────────────────────

  const conditionColOptions = (): { value: string; label: string }[] => {
    if (isPipelineMode) {
      const all = pipelineColOptions();
      return [{ value: '', label: '— select —' }, ...all.map(o => ({ value: o.value, label: `${o.label} (${o.type})` }))];
    }
    return [];
  };

  return (
    <Modal isOpen={isOpen} onClose={busy ? () => {} : onClose} size="lg">
      <Modal.Header>{`${rule ? 'Edit Detection Rule' : 'Create Detection Rule'}${collectionName ? ` — “${collectionName}”` : ''}`}</Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-y-5" data-testid="MOD_fa_rule_form">

          {/* ── What does this rule check? (FIRST) ── */}
          <StepGroup first title="What does this rule check?">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <ValidatedInput
                vh={fieldVh} name="name" code={109} required label="Rule name"
                value={name} onChange={e => setName(e.target.value)}
                placeholder="e.g. Login on a non-working day" disabled={busy} data-testid="INP_fa_rule_name"
              />
              <FormControl.SearchSelect
                label="Severity" value={severity}
                onChange={val => setSeverity((val as typeof severity) ?? 'Medium')}
                options={[
                  { value: 'Critical', label: 'Critical' },
                  { value: 'High', label: 'High' },
                  { value: 'Medium', label: 'Medium' },
                  { value: 'Low', label: 'Low' },
                ]}
                disabled={busy} data-testid="SEL_fa_rule_severity"
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <ValidatedInput
                vh={fieldVh} name="description" code={110} label="Description"
                value={description} onChange={e => setDescription(e.target.value)}
                placeholder="What this rule looks for and why" disabled={busy} data-testid="INP_fa_rule_description"
              />
              <FormControl.SearchSelect
                label="Link to a risk" value={riskId == null ? '' : String(riskId)}
                onChange={val => setRiskId(val ? Number(val) : null)}
                options={[{ value: '', label: 'None' }, ...riskOptions.map(r => ({ value: String(r.id), label: `${r.risk_code} — ${r.risk_name}` }))]}
                disabled={busy || mode === 'run'} data-testid="SEL_fa_rule_risk"
              />
            </div>
            {isPipelineMode && (
              (linkedSources.length > 0 || aggTargets.length > 0) ? (
                <FormControl.SearchSelect
                  label="Data source"
                  value={targetSel}
                  onChange={val => {
                    const sel = String(val ?? '');
                    setTargetSel(sel);
                    setSteps(prev => prev.map(s => ({ ...s, column: '', column_type: 'continuous', operator: 'gt', params: [''], negate: false })));
                    setEnrichedCols([]);
                    if (sel.startsWith('ds:')) loadEnriched(sel.slice(3), linkedSources);
                  }}
                  options={[
                    { value: '', label: '— select —' },
                    ...linkedSources.map(s => ({ value: `ds:${s.id}`, label: s.name })),
                    ...aggTargets.map(t => ({ value: `agg:${t.output_dataset_id}`, label: `Aggregated: ${t.name}` })),
                  ]}
                  disabled={busy || isLocked}
                  data-testid="SEL_fa_rule_data_source"
                />
              ) : (
                <div className="modal-error-banner" style={{ background: 'rgba(234,179,8,0.10)', borderColor: 'rgba(234,179,8,0.35)', color: 'rgb(146,64,14)' }}>
                  This pipeline has no data source bound yet — bind one under <strong>Data Sources</strong> first.
                </div>
              )
            )}
          </StepGroup>

          {isPipelineMode && noPipelineData && (linkedSources.length > 0 || aggTargets.length > 0) && (
            <div className="modal-error-banner" style={{ background: 'rgba(234,179,8,0.10)', borderColor: 'rgba(234,179,8,0.35)', color: 'rgb(146,64,14)' }}>
              No data uploaded for this pipeline yet — you can still define the rule, but it can only run once a sample file is uploaded under <strong>Data Sources</strong>.
            </div>
          )}

          {/* ── Detection conditions ── */}
          <StepGroup title="Detection conditions">
            {/* Single header row — shown once above all condition rows */}
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 40px 1fr 1.2fr 28px', gap: '0 10px', padding: '0 0 6px 0', borderBottom: '1px solid var(--border-subtle)' }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Column</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' }}>NOT</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Condition</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Value</span>
              <span />
            </div>

            {/* Condition rows — no repeated labels, no separators */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {steps.map((step, idx) => {
                const colOptions = isPipelineMode
                  ? conditionColOptions()
                  : [{ value: '', label: '— select —' }, ...colsForStep(step).map(c => ({ value: c.name, label: `${c.name} (${c.type})` }))];
                const currentColValue = step.column;
                const ops = operatorsFor(step.column_type);
                const opMeta = operatorByKind(step.operator);
                const pctParam = step.column_type === 'percent' && PERCENT_VALUE_OPS.has(step.operator);
                const canNegate = !ALREADY_NEGATED_OPS.has(step.operator);
                // All columns except self, with type shown in label.
                const otherColOptions = isPipelineMode
                  ? [{ value: '', label: '— select column —' }, ...pipelineColOptions().filter(o => o.value !== currentColValue).map(o => ({ value: o.value, label: `${o.label} (${o.type})` }))]
                  : [{ value: '', label: '— select column —' }, ...colsForStep(step).filter(c => c.name !== step.column).map(c => ({ value: c.name, label: `${c.name} (${c.type})` }))];

                // Type-compatible columns for same-type comparisons (col_gt, col_lt etc.)
                // Numeric family: numeric+continuous are mutually comparable.
                // Date: only date. Text/categorical: only text/categorical.
                const sameTypeFamily = (t: ColumnDataType): ColumnDataType[] => {
                  if (t === 'numeric' || t === 'continuous' || t === 'percent') return ['numeric', 'continuous', 'percent'];
                  if (t === 'date') return ['date'];
                  return ['text', 'categorical'];
                };
                const compatibleColOptions = isPipelineMode
                  ? [{ value: '', label: '— select column —' }, ...pipelineColOptions().filter(o => o.value !== currentColValue && sameTypeFamily(step.column_type).includes(o.type)).map(o => ({ value: o.value, label: `${o.label} (${o.type})` }))]
                  : [{ value: '', label: '— select column —' }, ...colsForStep(step).filter(c => c.name !== step.column && sameTypeFamily(step.column_type).includes(c.type)).map(c => ({ value: c.name, label: `${c.name} (${c.type})` }))];

                // Date-only columns for date-diff reference column pickers.
                const dateColOptions = isPipelineMode
                  ? [{ value: '', label: '— select date column —' }, ...pipelineColOptions().filter(o => o.value !== currentColValue && o.type === 'date').map(o => ({ value: o.value, label: `${o.label} (date)` }))]
                  : [{ value: '', label: '— select date column —' }, ...colsForStep(step).filter(c => c.name !== step.column && c.type === 'date').map(c => ({ value: c.name, label: `${c.name} (date)` }))];

                // Numeric-only columns for col_pct_above / percent_change_gt reference.
                const numericColOptions = isPipelineMode
                  ? [{ value: '', label: '— select numeric column —' }, ...pipelineColOptions().filter(o => o.value !== currentColValue && ['numeric', 'continuous', 'percent'].includes(o.type)).map(o => ({ value: o.value, label: `${o.label} (${o.type})` }))]
                  : [{ value: '', label: '— select numeric column —' }, ...colsForStep(step).filter(c => c.name !== step.column && ['numeric', 'continuous', 'percent'].includes(c.type)).map(c => ({ value: c.name, label: `${c.name} (${c.type})` }))];

                const valueCell = (
                  <>
                    {opMeta?.param_shape === 'none' && (
                      <span style={{ fontSize: 12, color: 'var(--text-tertiary)', fontStyle: 'italic' }}>—</span>
                    )}
                    {opMeta?.param_shape === 'single' && (() => {
                      const isNumericCol = ['numeric', 'continuous', 'percent'].includes(step.column_type);
                      return COL_PARAM_OPS.has(step.operator) ? (
                        <FormControl.SearchSelect label="" value={step.params[0] ?? ''} onChange={val => update(idx, { params: [String(val ?? '')] })} options={compatibleColOptions} disabled={busy} data-testid={`SEL_fa_rule_step_colcmp_${idx}`} />
                      ) : NUMBER_SINGLE_OPS.has(step.operator) ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <FormControl.Input label="" type="number" min="0.001" value={step.params[0] ?? ''} onChange={e => update(idx, { params: [e.target.value] })} placeholder={opMeta.param_hint ?? 'N'} disabled={busy} data-testid={`INP_fa_rule_step_val_${idx}`} />
                          {step.operator === 'within_last_days' && <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>days</span>}
                          {(step.operator === 'top_n' || step.operator === 'bottom_n') && <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>rows</span>}
                          {(step.operator === 'std_dev_above' || step.operator === 'std_dev_below') && <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>σ</span>}
                        </div>
                      ) : step.column_type === 'date' ? (
                        <FormControl.Input label="" type="date" value={step.params[0] ?? ''} onChange={e => update(idx, { params: [e.target.value] })} disabled={busy} data-testid={`INP_fa_rule_step_val_${idx}`} />
                      ) : isNumericCol ? (
                        <FormControl.Input label="" type="number" value={pctParam ? pctToDisplay(step.params[0] ?? '') : (step.params[0] ?? '')} onChange={e => update(idx, { params: [pctParam ? pctToStore(e.target.value) : e.target.value] })} placeholder={pctParam ? 'e.g. 25 for 25%' : (opMeta.param_hint ?? 'Value')} disabled={busy} data-testid={`INP_fa_rule_step_val_${idx}`} />
                      ) : (
                        <FormControl.Input label="" value={step.params[0] ?? ''} onChange={e => update(idx, { params: [e.target.value] })} placeholder={opMeta.param_hint ?? 'Value'} disabled={busy} data-testid={`INP_fa_rule_step_val_${idx}`} />
                      );
                    })()}
                    {opMeta?.param_shape === 'double' && !COL_PCT_OPS.has(step.operator) && (() => {
                      const isNumericCol = ['numeric', 'continuous', 'percent'].includes(step.column_type);
                      return DATE_DIFF_DAY_OPS.has(step.operator) ? (
                        <div className="fa-form-param-pair">
                          <FormControl.SearchSelect label="" value={step.params[0] ?? ''} onChange={val => update(idx, { params: [String(val ?? ''), step.params[1] ?? ''] })} options={dateColOptions} disabled={busy} data-testid={`SEL_fa_rule_step_datecol_${idx}`} />
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <FormControl.Input label="" type="number" min="0" value={step.params[1] ?? ''} onChange={e => update(idx, { params: [step.params[0] ?? '', e.target.value] })} placeholder="Days" disabled={busy} data-testid={`INP_fa_rule_step_val1_${idx}`} />
                            <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>days</span>
                          </div>
                        </div>
                      ) : HOUR_RANGE_OPS.has(step.operator) ? (
                        <div className="fa-form-param-pair">
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <FormControl.Input label="" type="number" min="0" max="23" value={step.params[0] ?? ''} onChange={e => { const v = Math.min(23, Math.max(0, Number(e.target.value))); update(idx, { params: [String(v), step.params[1] ?? ''] }); }} placeholder="0–23" disabled={busy} data-testid={`INP_fa_rule_step_val0_${idx}`} />
                            <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>h</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <FormControl.Input label="" type="number" min="0" max="23" value={step.params[1] ?? ''} onChange={e => { const v = Math.min(23, Math.max(0, Number(e.target.value))); update(idx, { params: [step.params[0] ?? '', String(v)] }); }} placeholder="0–23" disabled={busy} data-testid={`INP_fa_rule_step_val1_${idx}`} />
                            <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>h</span>
                          </div>
                        </div>
                      ) : step.column_type === 'date' ? (
                        <div className="fa-form-param-pair">
                          <FormControl.Input label="" type="date" value={step.params[0] ?? ''} onChange={e => update(idx, { params: [e.target.value, step.params[1] ?? ''] })} disabled={busy} data-testid={`INP_fa_rule_step_val0_${idx}`} />
                          <FormControl.Input label="" type="date" value={step.params[1] ?? ''} onChange={e => update(idx, { params: [step.params[0] ?? '', e.target.value] })} disabled={busy} data-testid={`INP_fa_rule_step_val1_${idx}`} />
                        </div>
                      ) : isNumericCol ? (
                        <div className="fa-form-param-pair">
                          <FormControl.Input label="" type="number" value={pctParam ? pctToDisplay(step.params[0] ?? '') : (step.params[0] ?? '')} onChange={e => update(idx, { params: [pctParam ? pctToStore(e.target.value) : e.target.value, step.params[1] ?? ''] })} placeholder={pctParam ? 'From %' : 'From'} disabled={busy} data-testid={`INP_fa_rule_step_val0_${idx}`} />
                          <FormControl.Input label="" type="number" value={pctParam ? pctToDisplay(step.params[1] ?? '') : (step.params[1] ?? '')} onChange={e => update(idx, { params: [step.params[0] ?? '', pctParam ? pctToStore(e.target.value) : e.target.value] })} placeholder={pctParam ? 'To %' : 'To'} disabled={busy} data-testid={`INP_fa_rule_step_val1_${idx}`} />
                        </div>
                      ) : (
                        <div className="fa-form-param-pair">
                          <FormControl.Input label="" value={step.params[0] ?? ''} onChange={e => update(idx, { params: [e.target.value, step.params[1] ?? ''] })} placeholder="From" disabled={busy} data-testid={`INP_fa_rule_step_val0_${idx}`} />
                          <FormControl.Input label="" value={step.params[1] ?? ''} onChange={e => update(idx, { params: [step.params[0] ?? '', e.target.value] })} placeholder="To" disabled={busy} data-testid={`INP_fa_rule_step_val1_${idx}`} />
                        </div>
                      );
                    })()}
                    {opMeta?.param_shape === 'csv' && !DATE_DIFF_YEAR_OPS.has(step.operator) && (
                      <ChipInput
                        value={step.params[0] ?? ''}
                        onChange={joined => update(idx, { params: [joined] })}
                        placeholder={opMeta.param_hint ?? 'Type and press Enter'}
                        disabled={busy}
                        validateChip={DATE_CSV_OPS.has(step.operator)
                          ? v => /^\d{4}-\d{2}-\d{2}$/.test(v) ? null : 'Date must be YYYY-MM-DD'
                          : undefined}
                        data-testid={`INP_fa_rule_chip_${idx}`}
                      />
                    )}
                    {opMeta?.param_shape === 'csv' && DATE_DIFF_YEAR_OPS.has(step.operator) && (() => {
                      // Structured UI: dateCol,lo,hi stored as comma-joined in params[0]
                      const parts = (step.params[0] ?? '').split(',').map(p => p.trim());
                      const [col0 = '', lo = '', hi = ''] = parts;
                      const setYearParts = (c: string, l: string, h: string) =>
                        update(idx, { params: [[c, l, h].join(',')] });
                      return (
                        <div className="fa-form-param-pair" style={{ flexWrap: 'wrap', gap: 6 }}>
                          <FormControl.SearchSelect
                            label=""
                            value={col0}
                            onChange={val => setYearParts(String(val ?? ''), lo, hi)}
                            options={dateColOptions}
                            disabled={busy}
                            data-testid={`SEL_fa_rule_step_yrcol_${idx}`}
                          />
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <FormControl.Input
                              label=""
                              type="number"
                              min="0"
                              max="150"
                              value={lo}
                              onChange={e => setYearParts(col0, e.target.value, hi)}
                              placeholder="Low yr"
                              disabled={busy}
                              data-testid={`INP_fa_rule_step_yrlo_${idx}`}
                              style={{ width: 72 }}
                            />
                            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>–</span>
                            <FormControl.Input
                              label=""
                              type="number"
                              min="0"
                              max="150"
                              value={hi}
                              onChange={e => setYearParts(col0, lo, e.target.value)}
                              placeholder="High yr"
                              disabled={busy}
                              data-testid={`INP_fa_rule_step_yrhi_${idx}`}
                              style={{ width: 72 }}
                            />
                            <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>yrs</span>
                          </div>
                        </div>
                      );
                    })()}
                    {opMeta?.param_shape === 'double' && COL_PCT_OPS.has(step.operator) && (
                      <div className="fa-form-param-pair">
                        <FormControl.SearchSelect label="" value={step.params[0] ?? ''} onChange={val => update(idx, { params: [String(val ?? ''), step.params[1] ?? ''] })} options={numericColOptions} disabled={busy} data-testid={`SEL_fa_rule_step_refcol_${idx}`} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <FormControl.Input label="" type="number" value={step.params[1] ?? ''} onChange={e => update(idx, { params: [step.params[0] ?? '', e.target.value] })} placeholder="% threshold" disabled={busy} data-testid={`INP_fa_rule_step_pct_${idx}`} />
                          <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>%</span>
                        </div>
                      </div>
                    )}
                    {opMeta?.param_shape === 'dataset_ref' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <FormControl.SearchSelect
                          label=""
                          value={step.params[0] ?? ''}
                          onChange={val => {
                            const pid = String(val ?? '');
                            update(idx, { params: [pid, ''] });
                            if (pid) loadRefCols(step.tempId, pid);
                          }}
                          options={[{ value: '', label: '— select pipeline —' }, ...refPipelines]}
                          disabled={busy}
                          data-testid={`SEL_fa_rule_step_refpipe_${idx}`}
                        />
                        <FormControl.SearchSelect
                          label=""
                          value={step.params[1] ?? ''}
                          onChange={val => update(idx, { params: [step.params[0] ?? '', String(val ?? '')] })}
                          options={[{ value: '', label: '— select column —' }, ...(refCols[step.tempId] ?? [])]}
                          disabled={busy || !step.params[0]}
                          data-testid={`SEL_fa_rule_step_refcol2_${idx}`}
                        />
                      </div>
                    )}
                  </>
                );

                return (
                  <div key={step.tempId} style={{ display: 'grid', gridTemplateColumns: '1.2fr 40px 1fr 1.2fr 28px', gap: '0 10px', alignItems: 'center', padding: '5px 0' }}>
                      {showDatasetPicker ? (
                        <FormControl.SearchSelect label="" value={step.target_dataset_id} onChange={val => onDatasetChange(idx, String(val ?? ''))} options={[{ value: '', label: '— select —' }, ...datasets.map(d => ({ value: d.id, label: d.file_name }))]} disabled={busy} data-testid={`SEL_fa_rule_step_dataset_${idx}`} />
                      ) : (
                        <FormControl.SearchSelect label="" value={currentColValue} onChange={val => onColumnChange(idx, String(val ?? ''))} options={colOptions} disabled={busy || colOptions.length <= 1} data-testid={`SEL_fa_rule_step_col_${idx}`} />
                      )}
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        {canNegate ? (
                          <input
                            type="checkbox"
                            checked={step.negate}
                            onChange={() => !busy && update(idx, { negate: !step.negate })}
                            data-testid={`BTN_fa_rule_step_negate_${idx}`}
                            title="Invert this condition (NOT)"
                            style={{ accentColor: '#dc2626', width: 14, height: 14, cursor: busy ? 'not-allowed' : 'pointer' }}
                          />
                        ) : <span />}
                      </div>
                      <FormControl.SearchSelect label="" value={step.operator} onChange={val => onOperatorChange(idx, (val as OperatorKind) ?? 'eq')} options={buildGroupedOpOptions(ops)} disabled={busy || !step.column} data-testid={`SEL_fa_rule_step_op_${idx}`} />
                      <div>{valueCell}</div>
                      <button type="button" className="tree-list-action tree-list-action--delete" disabled={busy || steps.length <= 1} title="Remove condition" onClick={() => removeStep(idx)} data-testid={`BTN_fa_rule_step_remove_${idx}`}>
                        <X size={13} />
                      </button>
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
              <Button type="button" variant="secondary-outline" icon={Plus} onClick={addStep} disabled={busy} data-testid="BTN_fa_rule_step_add">
                Add condition
              </Button>
            </div>
          </StepGroup>

          {error && <div className="modal-error-banner">{error}</div>}
        </div>
      </Modal.Body>
      <Modal.Footer>
        {rule && onDelete && (
          <Button
            variant="delete-soft"
            icon={Trash2}
            onClick={onDelete}
            disabled={busy}
            style={{ marginRight: 'auto' }}
            data-testid="BTN_fa_rule_delete_modal"
          >
            Delete
          </Button>
        )}
        <Button
          variant={rule ? 'update' : 'fill'}
          icon={rule ? Check : Save}
          onClick={submit}
          disabled={permLoading || !canModify || busy || !name.trim()}
          title={!canModify ? NOT_AUTHORISED_MSG : undefined}
          data-testid="BTN_fa_rule_submit"
        >
          {busy ? 'Saving…' : rule ? 'Update' : 'Submit'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RuleBuilderModal;
